import { useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, AlertTriangle, Landmark, Euro, User as UserIcon, ArrowRight, Eye } from "lucide-react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { canDo } from "@/lib/permissions";

/**
 * DAF — Reconciliação por Referência.
 *
 * Fluxo: o cliente aceita a proposta → sistema gera ``CAR-XXXXXXXX`` →
 * cliente coloca essa referência na transferência bancária. Ao processar
 * o extracto do banco, o DAF pesquisa aqui pela referência, valida o
 * cliente e o plano, e submete o valor recebido. O backend classifica
 * automaticamente como kickstart / remainder / adjustment consoante o
 * cumulativo já pago vs. plan.
 *
 * Também suporta o caso "pagamento por ATM sem referência" — o DAF pode
 * escolher ``Confirmar manualmente`` que abre uma pesquisa por nome/lead
 * ao invés de referência.
 */
export default function DAFReconcile() {
  const canReconcile = canDo("daf_reconcile");
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Transferência");
  const [receivedAt, setReceivedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [bankRef, setBankRef] = useState("");
  const [notes, setNotes] = useState("");
  const [overrideKind, setOverrideKind] = useState("");
  const [saving, setSaving] = useState(false);

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setLookup(null);
    try {
      const r = await api.get(`/daf/lookup-reference/${ref.trim().toUpperCase()}`);
      setLookup(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Referência não encontrada");
    } finally {
      setLoading(false);
    }
  };

  const reconcile = async () => {
    const amt = parseFloat(amount);
    if (!lookup || !(amt > 0)) {
      toast.error("Indique um valor positivo.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/daf/reconcile-by-reference", {
        reference: lookup.reference,
        amount: amt,
        received_at: receivedAt,
        method,
        bank_ref: bankRef || null,
        notes: notes || null,
        override_kind: overrideKind || null,
      });
      const inferred = r.data.inferred_kind || "adjustment";
      const map = { kickstart: "Caução (kickstart)", remainder: "Remanescente", adjustment: "Ajuste" };
      toast.success(`Pagamento reconciliado · ${map[inferred] || inferred}`);
      // Refresh lookup so the DAF sees the updated ledger
      await search();
      setAmount("");
      setBankRef("");
      setNotes("");
      setOverrideKind("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a reconciliar");
    } finally {
      setSaving(false);
    }
  };

  const plan = lookup?.plan || {};
  const paid = lookup?.paid || {};
  const outstanding = paid.outstanding || 0;

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-8" data-testid="daf-reconcile-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">DAF</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">{canReconcile ? "Reconciliar Pagamento" : "Consulta de Pagamento"}</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
          {canReconcile
            ? "Introduza a referência única que o cliente colocou na transferência bancária. O sistema identifica automaticamente o lead e classifica o pagamento (caução, remanescente ou ajuste)."
            : "Consulte o estado de pagamento de um cliente pela referência. Apenas o DAF/Direção pode reconciliar novos pagamentos."}
        </p>
        {!canReconcile && (
          <div data-testid="daf-readonly-banner" className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold bg-muted text-foreground border border-border rounded-full px-3 py-1.5">
            <Eye className="w-3 h-3" /> Modo consulta
          </div>
        )}
      </div>

      {/* Search box */}
      <form onSubmit={search} className="bg-card border border-border rounded-2xl p-5 lg:p-6 space-y-3" data-testid="daf-search-form">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Referência (CAR-XXXXXXXX)</label>
        <div className="flex gap-2 flex-wrap">
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value.toUpperCase())}
            placeholder="Ex.: CAR-A1B2C3D4"
            data-testid="daf-reference-input"
            className="flex-1 min-w-[220px] bg-muted/40 border border-border rounded-full px-5 py-3 font-mono text-sm uppercase focus:outline-none focus:border-primary"
          />
          <button
            type="submit"
            disabled={loading || !ref.trim()}
            data-testid="daf-search-btn"
            className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-5 py-3 font-semibold text-sm disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Procurar
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Cliente pagou por ATM sem referência? Localize o lead pelo <Link to="/leads" className="underline text-foreground">nome</Link> e use o portal DAF público do lead.
        </p>
      </form>

      {/* Lead + plan summary */}
      {lookup && (
        <section className="bg-card border border-[hsl(var(--success))]/25 rounded-2xl p-5 lg:p-6 space-y-4" data-testid="daf-lookup-result">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold">Referência válida</div>
              <div className="font-mono font-bold text-foreground text-lg" data-testid="lookup-reference">{lookup.reference}</div>
            </div>
            <Link
              to={`/leads/${lookup.lead_id}`}
              className="ml-auto text-[11px] bg-muted hover:bg-muted border border-border text-foreground rounded-full px-3 py-1.5 font-semibold inline-flex items-center gap-1"
            >
              Abrir lead <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Cliente</div>
              <div className="text-foreground font-semibold" data-testid="lookup-lead-name">{lookup.lead_name || "—"}</div>
              {lookup.lead_phone && <div className="text-[11px] text-muted-foreground">{lookup.lead_phone}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Plano · Total</div>
              <div className="font-mono text-foreground font-bold">{plan.total_amount ? `${plan.total_amount} €` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Kickstart</div>
              <div className="font-mono text-foreground font-bold">{plan.kickstart_amount ? `${plan.kickstart_amount} €` : "—"}</div>
            </div>
          </div>

          {/* Payment ledger */}
          <div className="border-t border-border pt-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <StatCard label="Kickstart pago" value={`${paid.kickstart || 0} €`} testid="stat-paid-kickstart" />
              <StatCard label="Total pago" value={`${paid.total || 0} €`} testid="stat-paid-total" />
              <StatCard label="Em falta" value={`${outstanding} €`} highlight={outstanding > 0} testid="stat-outstanding" />
              <StatCard label="Pagamentos" value={lookup.payments?.length || 0} testid="stat-payments-count" />
            </div>
          </div>

          {outstanding <= 0 && (
            <div className="bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-lg p-3 text-[hsl(var(--success))] text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Este cliente já pagou o total. Novos pagamentos serão classificados como <b>ajuste</b>.
            </div>
          )}

          {/* Reconcile form — só visível para roles com permissão de escrita */}
          {canReconcile && (
          <div className="border-t border-border pt-4 space-y-3" data-testid="daf-reconcile-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1"><Euro className="w-3 h-3" /> Valor recebido</label>
                <input
                  type="number" step="0.01" min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="daf-amount-input"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Data</label>
                <input
                  type="date"
                  value={receivedAt}
                  onChange={(e) => setReceivedAt(e.target.value)}
                  data-testid="daf-date-input"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Método</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  data-testid="daf-method-select"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm mt-1"
                >
                  <option>Transferência</option>
                  <option>ATM</option>
                  <option>MB WAY</option>
                  <option>Débito directo</option>
                  <option>Numerário</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Ref. banco (opcional)</label>
                <input
                  value={bankRef}
                  onChange={(e) => setBankRef(e.target.value)}
                  placeholder="ID do movimento no extracto"
                  data-testid="daf-bankref-input"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Classificação (opcional — deixe vazio para automático)</label>
              <div className="flex gap-2 flex-wrap mt-1">
                {["", "kickstart", "remainder", "adjustment"].map((k) => (
                  <button
                    key={k || "auto"}
                    type="button"
                    onClick={() => setOverrideKind(k)}
                    data-testid={`daf-kind-${k || "auto"}`}
                    className={`text-xs rounded-full px-3 py-1.5 border ${overrideKind === k ? "bg-foreground text-background border-primary" : "bg-card text-muted-foreground border-border hover:bg-muted/40"}`}
                  >
                    {k === "" ? "Automático" : k === "kickstart" ? "Caução" : k === "remainder" ? "Remanescente" : "Ajuste"}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Notas (opcional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={400}
                data-testid="daf-notes-input"
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm mt-1 min-h-[60px]"
                placeholder="Ex.: pagamento ATM sem referência — verificado no extracto"
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={reconcile}
                disabled={saving || !amount}
                data-testid="daf-submit-btn"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-6 py-3 font-semibold text-sm disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Landmark className="w-4 h-4" />}
                Confirmar pagamento
              </button>
            </div>
          </div>
          )}

          {/* Existing payments list */}
          {lookup.payments?.length > 0 && (
            <div className="border-t border-border pt-4" data-testid="daf-payments-history">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Histórico</div>
              <div className="space-y-1.5">
                {lookup.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs bg-muted/40 border border-border rounded-lg px-3 py-2">
                    <div>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-muted text-foreground border border-border uppercase tracking-widest text-[9px] font-bold mr-2">{p.kind}</span>
                      <span className="font-mono font-bold">{p.amount} €</span>
                    </div>
                    <div className="text-muted-foreground">{p.received_at} · {p.method}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border p-3 ${highlight ? "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25" : "bg-muted/40 border-border"}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`font-mono text-base font-bold mt-0.5 ${highlight ? "text-[hsl(var(--warning))]" : "text-foreground"}`}>{value}</div>
    </div>
  );
}
